import type { KeyboardEvent } from 'react';

const ITEM = '[role="treeitem"]';

function level(item: HTMLElement): number {
  return Number(item.getAttribute('aria-level'));
}

function chevronOf(item: HTMLElement): HTMLElement | null {
  return item.querySelector<HTMLElement>('button');
}

function stepTarget(key: string, items: HTMLElement[], index: number): HTMLElement | null {
  if (key === 'ArrowDown') return items[Math.min(index + 1, items.length - 1)];
  if (key === 'ArrowUp') return items[Math.max(index - 1, 0)];
  if (key === 'Home') return items[0];
  return key === 'End' ? items[items.length - 1] : null;
}

/** The row to move focus to for a navigation key, or null when the key is not
 *  one that moves focus. Expanding or collapsing is done by clicking the row's
 *  own chevron, so the expanded state stays in one place. */
function moveTarget(key: string, items: HTMLElement[], index: number): HTMLElement | null {
  const current = items[index];
  const expanded = current.getAttribute('aria-expanded') === 'true';
  if (key === 'ArrowRight' && expanded) return items[index + 1] ?? null;
  if (key === 'ArrowLeft' && !expanded) {
    return [...items.slice(0, index)].reverse().find((i) => level(i) < level(current)) ?? null;
  }
  return stepTarget(key, items, index);
}

/** Arrow-key navigation for the open tree panel. */
export function handleTreeKeys(e: KeyboardEvent<HTMLElement>): void {
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>(ITEM)];
  const index = items.findIndex((i) => i === document.activeElement);
  if (index < 0) return;
  const item = items[index];
  const collapsed = item.getAttribute('aria-expanded') === 'false';
  const expanded = item.getAttribute('aria-expanded') === 'true';
  if ((e.key === 'ArrowRight' && collapsed) || (e.key === 'ArrowLeft' && expanded)) {
    e.preventDefault();
    chevronOf(item)?.click();
    return;
  }
  const target = moveTarget(e.key, items, index);
  if (!target) return;
  e.preventDefault();
  target.focus();
}
